import React, { useState, useEffect } from 'react';
import './Admin.css';
import LoginForm from '../components/auth/LoginForm';
import { authApi } from '../api/phpApi';

// Management Components
import AdminInstitute from '../components/admin/AdminInstitute';
import AdminGSIDASection from '../components/admin/AdminGSIDASection';
import AdminFormations from '../components/admin/AdminFormations';
import AdminAcademicChallenge from '../components/admin/AdminAcademicChallenge';
import AdminAPSISSection from '../components/admin/AdminAPSISSection';
import AdminExperts from '../components/admin/AdminExperts';
import AdminResearchCommittee from '../components/admin/AdminResearchCommittee';
import AdminPublications from '../components/admin/AdminPublications';
import AdminRevueRIGSIDA from '../components/admin/AdminRevueRIGSIDA';

// Global Configuration Components
import AdminHeroSlides from '../components/admin/AdminHeroSlides';
import AdminNavigation from '../components/admin/AdminNavigation';
import AdminUsers from '../components/admin/AdminUsers';

// Icons
import { 
  FiHome, FiGlobe, FiBookOpen, FiAward, FiShield, 
  FiUsers, FiCheckCircle, FiFileText, FiBook, FiSliders,
  FiCompass, FiUserCheck, FiLogOut, FiExternalLink, 
  FiMenu, FiX, FiSearch
} from 'react-icons/fi';

export default function Admin() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMenuId, setActiveMenuId] = useState('institute');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const currentUser = authApi.getCurrentUser();
    if (currentUser && authApi.isAuthenticated()) {
      setUser(currentUser);
    }
    setLoading(false);
  }, []);

  const handleLoginSuccess = (loggedInUser) => {
    setUser(loggedInUser);
  };

  const handleLogout = () => {
    authApi.logout();
    setUser(null);
  };

  // Sidebar Menu Items matching the user request
  const menuItems = [
    {
      group: 'GESTION DU SITE WEB',
      items: [
        {
          id: 'institute',
          label: 'Institute',
          subtitle: 'Missions, Domaines, Fondateur, Vision...',
          icon: <FiHome size={18} />,
          component: AdminInstitute,
          badge: '9 sections'
        },
        {
          id: 'gsida',
          label: 'GSIDA',
          subtitle: 'Congrès, Speakers, Programme, Partenaires...',
          icon: <FiGlobe size={18} />,
          component: AdminGSIDASection,
          badge: '7 comp.'
        },
        {
          id: 'formations',
          label: 'Formations',
          subtitle: 'Executive Programs & Certifications',
          icon: <FiBookOpen size={18} />,
          component: AdminFormations,
          badge: 'Exécutif'
        },
        {
          id: 'challenge',
          label: 'GSIDA Academic Challenge',
          subtitle: 'Doctoral, Awards, Hackathon...',
          icon: <FiAward size={18} />,
          component: AdminAcademicChallenge,
          badge: '4 volets'
        },
        {
          id: 'apsis',
          label: 'APSIS 2026',
          subtitle: 'Sommet Sécurité, Exposants, Startups...',
          icon: <FiShield size={18} />,
          component: AdminAPSISSection,
          badge: 'Sommet'
        },
        {
          id: 'experts',
          label: 'Experts',
          subtitle: 'Réseau mondial de spécialistes & consultants',
          icon: <FiUsers size={18} />,
          component: AdminExperts,
          badge: 'Réseau'
        },
        {
          id: 'comite-scientifique',
          label: 'Comité scientifique',
          subtitle: 'Membres & Chercheurs du conseil scientifique',
          icon: <FiCheckCircle size={18} />,
          component: AdminResearchCommittee,
          badge: 'Académique'
        },
        {
          id: 'publications',
          label: 'Publications',
          subtitle: 'Actes de congrès, Livres & Rapports',
          icon: <FiFileText size={18} />,
          component: AdminPublications,
          badge: 'PDF/Actes'
        },
        {
          id: 'revue-rigsida',
          label: 'Revue RIGSIDA',
          subtitle: 'Revue internationale, Résumé & Édition',
          icon: <FiBook size={18} />,
          component: AdminRevueRIGSIDA,
          badge: 'Revue'
        }
      ]
    },
    {
      group: 'PARAMÈTRES & SYSTÈME',
      items: [
        {
          id: 'hero-slides',
          label: 'Slides Accueil',
          subtitle: 'Bannières et carrousel page principale',
          icon: <FiSliders size={18} />,
          component: AdminHeroSlides
        },
        {
          id: 'navigation',
          label: 'Navigation & Header',
          subtitle: 'Menus, liens et structure de la barre du haut',
          icon: <FiCompass size={18} />,
          component: AdminNavigation
        },
        {
          id: 'users',
          label: 'Comptes & Utilisateurs',
          subtitle: 'Gestion des accès et administrateurs',
          icon: <FiUserCheck size={18} />,
          component: AdminUsers
        }
      ]
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
        <p style={{ fontSize: 16, color: '#475569', fontWeight: 600 }}>Chargement du panneau d'administration...</p>
      </div>
    );
  }

  // If not authenticated, show login form
  if (!user) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  // Find active item
  let activeItem = null;
  for (const group of menuItems) {
    const found = group.items.find(i => i.id === activeMenuId);
    if (found) {
      activeItem = found;
      break;
    }
  }
  const ActiveComponent = activeItem?.component || AdminInstitute;

  // Filter items by search query if any
  const filteredGroups = menuItems.map(group => ({
    ...group,
    items: group.items.filter(item => 
      item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.subtitle && item.subtitle.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  })).filter(group => group.items.length > 0);

  return (
    <div className="admin-layout-root">
      {/* Mobile Drawer Backdrop */}
      {mobileMenuOpen && (
        <div 
          className="admin-sidebar-backdrop" 
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside className={`admin-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        {/* Brand Header */}
        <div className="admin-sidebar-header">
          <div className="admin-brand-logo-area">
            <div className="admin-brand-icon-box">
              <span>IA</span>
            </div>
            <div className="admin-brand-titles">
              <h2>IAMAR ADMIN</h2>
              <span>Panel de Contrôle</span>
            </div>
          </div>
          {mobileMenuOpen && (
            <button 
              onClick={() => setMobileMenuOpen(false)}
              style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
            >
              <FiX size={22} />
            </button>
          )}
        </div>

        {/* Search */}
        <div className="admin-sidebar-search-box">
          <div className="admin-sidebar-search-inner">
            <FiSearch size={14} className="admin-sidebar-search-icon" />
            <input 
              type="text" 
              placeholder="Rechercher une section..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Navigation list */}
        <nav className="admin-sidebar-nav">
          {filteredGroups.map((group, gIdx) => (
            <div key={gIdx} style={{ marginBottom: 16 }}>
              <span className="admin-nav-group-title">{group.group}</span>
              {group.items.map(item => (
                <button
                  key={item.id}
                  className={`admin-nav-item ${activeMenuId === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveMenuId(item.id);
                    setMobileMenuOpen(false);
                  }}
                  title={item.subtitle || item.label}
                >
                  <div className="admin-nav-item-left">
                    <span className="admin-nav-item-icon">{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                  {item.badge && (
                    <span className="admin-nav-badge">{item.badge}</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* User Footer */}
        <div className="admin-sidebar-footer">
          <div className="admin-sidebar-user-info">
            <div className="admin-user-avatar">
              {(user.username || user.email || 'A')[0].toUpperCase()}
            </div>
            <div className="admin-user-details">
              <div className="admin-user-name">
                {user.username || user.email}
              </div>
              <div className="admin-user-role">
                {user.role || 'Super Admin'}
              </div>
            </div>
          </div>

          <button 
            className="admin-btn-logout-mini"
            onClick={handleLogout}
            title="Se déconnecter"
          >
            <FiLogOut size={14} />
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div className="admin-main-wrap">
        {/* Top Navbar */}
        <header className="admin-top-navbar">
          <div className="admin-top-nav-left">
            <button 
              className="admin-mobile-toggle-btn"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Toggle Menu"
            >
              <FiMenu size={20} />
            </button>
            <div className="admin-top-breadcrumbs">
              <span>Administration</span>
              <span>/</span>
              <span className="active-crumb">{activeItem?.label}</span>
            </div>
          </div>

          <div className="admin-top-nav-right">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="admin-btn-visit-site"
              title="Ouvrir le site web dans un nouvel onglet"
            >
              <FiExternalLink size={15} /> Voir le site
            </a>
          </div>
        </header>

        {/* Page Content */}
        <main className="admin-content-container">
          {ActiveComponent && <ActiveComponent />}
        </main>
      </div>
    </div>
  );
}
